/**
 * Neuversand-Schutz im Browser (Antoine, 20.10.2026, V-1197): der Server antwortet auf
 * `POST /api/events/:id/offers/send` und `.../offers/resend` mit 409 `adresse_unzustellbar`, wenn
 * die Zieladresse die ist, an die die jüngste Mail als unzustellbar zurückkam. Diese Datei erkennt
 * die Antwort am geworfenen Fehler (`toApiError` hängt `code` und `body` an) und kennt die eine
 * Regel, die der Dialog braucht: „Trotzdem senden“ gibt es nur bei vollem Postfach.
 */
export type UnzustellbarGrund = "postfach_existiert_nicht" | "adresse_gesperrt" | "postfach_voll" | "unbekannt";

export interface UnzustellbarInfo {
  grund: UnzustellbarGrund;
  grundText: string;
  adresse: string;
  letzterVersuchAm: string | null;
  /** Der Kunde des Vorgangs, dessen Adresse zu korrigieren ist (`PATCH /api/customers/:id`). */
  kundeId: string | null;
}

/** Ist dieser Fehler die 409-Antwort `adresse_unzustellbar`? Dann die Angaben daraus, sonst `null`. */
export function alsUnzustellbar(fehler: unknown): UnzustellbarInfo | null {
  if (!fehler || typeof fehler !== "object") return null;
  const f = fehler as { code?: unknown; statusCode?: unknown; body?: unknown };
  if (f.code !== "adresse_unzustellbar" || f.statusCode !== 409) return null;
  const b = (f.body ?? {}) as Record<string, unknown>;
  const adresse = typeof b.adresse === "string" ? b.adresse : "";
  const grund = (["postfach_existiert_nicht", "adresse_gesperrt", "postfach_voll", "unbekannt"] as const).find((g) => g === b.grund) ?? "unbekannt";
  return {
    grund,
    grundText: typeof b.grundText === "string" ? b.grundText : "Nicht zugestellt",
    adresse,
    letzterVersuchAm: typeof b.letzterVersuchAm === "string" ? b.letzterVersuchAm : null,
    kundeId: typeof b.kundeId === "string" ? b.kundeId : null,
  };
}

/** „Trotzdem senden“ nur, wo die Lage vorübergehend sein kann (der Server prüft dieselbe Regel). */
export function trotzdemMoeglich(info: UnzustellbarInfo): boolean {
  return info.grund === "postfach_voll";
}

/** Grob gültige E-Mail-Adresse (die genaue Prüfung macht der Server am Kunden). */
export function sieheAusWieAdresse(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text.trim());
}
